import * as React from 'react'
import { useCallback } from 'react'
import TextField from '@mui/material/TextField'
import { styles } from './hooks/useStyles'
import { CancelButton, LightTooltip } from '@sales-center/components'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import LockIcon from '@mui/icons-material/Lock'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import {
  deleteEditFunnelStage,
  setEditFunnelStageName,
} from '@user/store/salesFunnel/editSlice'
import { DisqualifyReasonList } from './DisqualifyReasonList'
import { ISalesFunnelStageEditModel } from '@user/store/salesFunnel/typings'
import Box from '@mui/material/Box'

type Props = {
  canHaveReasons: boolean
  stage: ISalesFunnelStageEditModel
  sectionName: string
}

export function SalesFunnelStage(props: Props) {
  const { stage, canHaveReasons, sectionName } = props

  const {
    id,
    sectionId,
    name,
    isValid,
    locked,
    recordCount,
    error,
    persistent,
  } = stage

  const dispatch = useAppDispatch()

  const onChangeStageName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      dispatch(
        setEditFunnelStageName({
          id,
          name: e.target.value,
        }),
      )
    },
    [dispatch, id],
  )

  const onDeleteStage = useCallback(() => {
    dispatch(deleteEditFunnelStage(id))
  }, [dispatch, id])

  return (
    <Box sx={styles.draggable}>
      <Box sx={styles.row}>
        <Box sx={styles.actionContainer}>
          <div>
            <DragIndicatorIcon
              sx={{ cursor: 'grab' }}
              fontSize="small"
              data-test-id="DragIndicatorIcon"
            />
          </div>
          {persistent || locked || recordCount > 0 ? (
            <LightTooltip
              title={
                recordCount > 0
                  ? `This stage cannot be deleted, it has ${recordCount} ${
                      recordCount > 1 ? 'records' : 'record'
                    } associated with it.`
                  : `This stage is locked and cannot be deleted.`
              }
            >
              <LockIcon fontSize="small" sx={styles.lockIcon} />
            </LightTooltip>
          ) : (
            <CancelButton onClick={onDeleteStage} sx={styles.deleteButton} />
          )}
        </Box>
        <TextField
          sx={{
            ...styles.textField,
            ...(canHaveReasons && styles.textFieldSquare),
          }}
          label={locked ? undefined : 'Name'}
          variant="outlined"
          required
          size="small"
          disabled={locked}
          value={name}
          onChange={onChangeStageName}
          error={!isValid}
          helperText={error}
          inputProps={{
            'data-test-id': `funnelStage${
              sectionName.includes('Lead') ? 'Lead' : 'Opp'
            }NameInput`,
          }}
        />
      </Box>
      {canHaveReasons && <DisqualifyReasonList sectionId={sectionId} />}
    </Box>
  )
}
